import { useEffect, useState } from "react"
import {
  probeLayerService,
  type LayerServiceMetadata,
} from "@/v2-backoffice-client/layer/service/layer.service.metadata"

export type LayerServiceMetadataState = {
  metadata: LayerServiceMetadata | null
  error: string | null
  isLoading: boolean
}

const IDLE: LayerServiceMetadataState = {
  metadata: null,
  error: null,
  isLoading: false,
}

/**
 * Probes a layer-service url (ArcGIS REST or WMS) and returns normalized
 * metadata for the guided widgets and the create flow. Null url = idle.
 * The underlying fetches are cached per url, so several consumers of the
 * same service cost one request.
 */
export function useLayerServiceMetadata(
  url: string | null,
): LayerServiceMetadataState {
  const [state, setState] = useState<LayerServiceMetadataState>(IDLE)

  useEffect(() => {
    if (!url) {
      setState(IDLE)
      return
    }

    let cancelled = false
    setState({ metadata: null, error: null, isLoading: true })

    probeLayerService(url)
      .then((metadata) => {
        if (!cancelled) setState({ metadata, error: null, isLoading: false })
      })
      .catch((probeError: unknown) => {
        if (cancelled) return
        // esri request errors are not native Errors; read structurally.
        const message =
          (probeError as { message?: string } | null)?.message ??
          "Could not reach the service."
        setState({ metadata: null, error: message, isLoading: false })
      })

    return () => {
      cancelled = true
    }
  }, [url])

  return state
}
